// Call Sheet's result screen (CLAUDE.md: Result screen order).
//
// The line, the answer (title, year, poster and cast), "Guesses", Share, then
// the aggregate panels, then the next game. One 24px gap between every section.
//
// No countdown: that belongs to the daily, and Call Sheet is a pool game with no
// tomorrow to count towards.
//
// The aggregate panels need results posted to a server and a minimum number of
// players on the puzzle — 20 for a pool game, against the daily's 50, because
// everyone meets a pool puzzle at a different point in their own run. Neither
// exists yet, so this screen is the state CLAUDE.md calls the hidden version.
import { useRef, useState } from 'react';
import { GameHeader } from '../../components/GameHeader.tsx';
import { Distribution, MISSED, guessLabel, type DistRow } from '../../components/Distribution.tsx';
import { PREVIEW, CROWD } from '../../dev/preview.ts';
import { distributionRows, pct, playersLabel, usePuzzleStats, type CallSheetTitle } from '../../lib/puzzle-stats.ts';
import { GuessList } from './CallSheetGame.tsx';
import { byId, type TitleRecord } from './titles.ts';
import { copyToClipboard, shareText } from './share.ts';
import { castForResult, directorLabel, posterUrl, roleLabel, yearLabel, MAX_GUESSES, type CallSheetAnswer, type CallSheetPlay } from './types.ts';
import type { Guess } from './progress.ts';
import { useNextPuzzle } from '../../lib/next-puzzle.ts';
import { NextButton } from '../../components/NextButton.tsx';
import '../../components/result.css';
import { usePosterFit } from './poster-fit.ts';
import './CallSheet.css';

interface Props {
  /** The server's verdict on a finished game: the answer is present. */
  play: CallSheetPlay & { answer: CallSheetAnswer };
  guesses: Guess[];
  list: TitleRecord[];
  /** A replay: no Share, because a replay is not a result. */
  replay?: boolean;
}

export function Result({ play, guesses, list, replay = false }: Props) {
  const [shareLabel, setShareLabel] = useState('Share');
  const won = play.won;
  // Each game's own next puzzle for this device, never this one, or its
  // countdown once every released one is finished (lib/next-puzzle.ts).
  const daily = useNextPuzzle('running-order');
  const next = useNextPuzzle('call-sheet', play.number);
  const answerRecord: TitleRecord = { id: play.answer.id, kind: play.answer.kind, title: play.answer.title, year: play.answer.year };

  async function share() {
    const ok = await copyToClipboard(shareText(play.number, won, guesses.length));
    setShareLabel(ok ? 'Copied' : "Couldn't copy");
    setTimeout(() => setShareLabel('Share'), 2000);
  }

  const own = won ? guessLabel(guesses.length) : MISSED;
  const stats = usePuzzleStats<CallSheetTitle>('call-sheet', play.number, guesses, !PREVIEW);
  const real = !PREVIEW && stats?.shown ? stats : null;
  const rows: DistRow[] = PREVIEW
    ? [
      ...CROWD.guesses.map((pct, i) => ({ label: guessLabel(i + 1), value: `${pct}%`, pct })),
      { label: MISSED, value: `${CROWD.missed}%`, pct: CROWD.missed }
    ].map((row) => ({ ...row, own: row.label === own }))
    : real ? distributionRows(real.distribution ?? [], real.players, own) : [];
  const players = PREVIEW ? CROWD.players : real?.players ?? 0;
  // The five titles players gave first, the answer among them where it was.
  const firstGuesses = PREVIEW
    ? CROWD.firstGuesses.map((row) => {
      const record = byId(row.id, row.kind, list, answerRecord);
      return { key: `${row.kind}-${row.id}`, title: record?.title ?? '—', pct: row.pct,
               correct: row.kind === play.answer.kind && row.id === play.answer.id };
    })
    : (real?.titles ?? []).map((row) => ({
      key: `${row.kind}-${row.id}`, title: row.title ?? 'Title no longer available',
      pct: pct(row.count, players), correct: row.correct
    }));

  const poster = posterUrl(play.answer.poster);
  const year = yearLabel(play.answer);
  const director = directorLabel(play.answer);
  const cast = castForResult(play.answer.cast, play.actors).slice(0, 8);
  // The poster grows to sit level with the cast where the cast is taller.
  const revealRef = useRef<HTMLDivElement>(null);
  usePosterFit(revealRef, play.number);

  return (
    <main className="column result cs-result">
      <GameHeader game="Call Sheet" number={play.number} />

      {/* The same line, in the same place, at the same size as the instruction
          it replaces. A win gets its score; a loss gets the answer headed,
          because the guess list below already says it was missed. */}
      {won
        ? <p className="panel-line cs-line">Correct in {guesses.length} of {MAX_GUESSES}</p>
        : <h2 className="panel-line cs-line">The correct answer:</h2>}

      <section className="cs-answer">
        {/* The title alone, with the year under it in smaller muted text. */}
        <h1 className="cs-answer-title">{play.answer.title}</h1>
        {year && <p className="cs-answer-year">{year}</p>}
        {/* Under the year and set like it. Rendered only when there is a name,
            so a title TMDB has no director for leaves no gap rather than an
            empty line. */}
        {director && <p className="cs-answer-director">{director}</p>}

        <div className="cs-reveal" ref={revealRef}>
          <div className="cs-answer-poster">
            {poster
              ? <img className="cs-answer-poster-img" src={poster} alt="" />
              : <span className="cs-answer-poster-empty" aria-hidden="true">Poster</span>}
          </div>
          {/* **Two real columns, read down the first then the second**, rather
              than one grid flowing by column. On a wide screen they face each
              other across the page's centre line and are aligned towards it,
              which only works if each is its own box; on a phone the second is
              hidden and the first is the whole cast. The four the puzzle
              revealed are always among the eight — see castForResult. */}
          <h2 className="heading cs-cast-heading">Cast</h2>
          <div className="cs-cast-col cs-cast-col1">
            {cast.slice(0, 4).map((member) => (
              <p className="cs-cast-row" key={member.name}>
                <span className="cs-cast-name">{member.name}</span>
                {member.role && <span className="cs-cast-role">{roleLabel(member.role)}</span>}
              </p>
            ))}
          </div>
          <div className="cs-cast-col cs-cast-col2">
            {cast.slice(4, 8).map((member) => (
              <p className="cs-cast-row" key={member.name}>
                <span className="cs-cast-name">{member.name}</span>
                {member.role && <span className="cs-cast-role">{roleLabel(member.role)}</span>}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Always shown, even on a first-guess win: the section that lists every
          guess is the record of the round. */}
      <GuessList guesses={guesses} results={play.results} list={list} answer={answerRecord} heading="Guesses" />

      {!replay && <button type="button" className="btn btn-primary btn-block" data-share onClick={share}>{shareLabel}</button>}

      {/* Below the player threshold the panels are hidden and nothing takes
          their place (owner, 12 October). */}
      {(PREVIEW || real) && (
        <section className="panel">
          <div className="panel-head">
            <h2 className="heading">Guess distribution</h2>
            <span className="panel-count">{playersLabel(players)}</span>
          </div>
          <Distribution rows={rows} />
        </section>
      )}

      {/* The mirror of Running Order's "Misplaced by players": what everyone
          reached for first, the right answer included. */}
      {(PREVIEW || real) && firstGuesses.length > 0 && (
        <section className="panel">
          <h2 className="heading">Title statistics</h2>
          <p className="panel-sub">Top titles for guess 1</p>
          <div className="title-stats">
            {/* The right answer in the pale accent, as a correctly placed title
                is on Running Order's panel. */}
            {firstGuesses.map((row) => (
              <div className={`title-stat${row.correct ? ' is-right' : ''}`} key={row.key}>
                <span className="title-stat-name">{row.title}</span>
                <span className="title-stat-pct">{row.pct}%</span>
                <span className="title-stat-bar" style={{ width: `${row.pct}%` }} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The other game first, because it is the one the player has not just
          played, then the next Call Sheet this device has not finished. Each
          appears only where there is one: never a filler button. */}
      {(daily?.pick || next?.pick) && (
        <div className="next-row">
          <NextButton game="running-order" next={daily} />
          <NextButton game="call-sheet" next={next} />
        </div>
      )}
    </main>
  );
}
